/**
 * Pure helpers behind Advanced Copy (components/AdvancedCopyDialog.tsx): turn
 * the selected rows' cell values into clipboard text. Kept free of React and
 * MUI so the formats can be unit tested.
 */

export type CopyFormat = "tsv" | "csv" | "markdown" | "json" | "list";
export type QuoteMode = "auto" | "always" | "never";
export type ListQuote = "none" | "single" | "double";

export interface AdvancedCopyOptions {
  format: CopyFormat;
  /** tsv / csv: first line holds the column headers */
  includeHeader: boolean;
  /** Header text (and JSON keys): the column's name or its field id */
  headerSource: "label" | "field";
  /** Values as the grid shows them, or the underlying raw values */
  valueSource: "formatted" | "raw";
  /** Written for empty cells (null, undefined or "") */
  nullText: string;
  /** tsv / csv / markdown: a leading "#" column numbering the rows */
  includeRowNumbers: boolean;
  /** tsv / csv: when values get wrapped in double quotes */
  quoteMode: QuoteMode;
  /** list: text between values */
  listSeparator: string;
  /** list: quotes around each value */
  listQuote: ListQuote;
  /** list: drop repeated values, keeping the first */
  distinct: boolean;
}

export const DEFAULT_ADVANCED_COPY_OPTIONS: AdvancedCopyOptions = {
  format: "tsv",
  includeHeader: true,
  headerSource: "label",
  valueSource: "formatted",
  nullText: "",
  includeRowNumbers: false,
  quoteMode: "auto",
  listSeparator: ", ",
  listQuote: "none",
  distinct: false,
};

export const COPY_FORMATS: {
  value: CopyFormat;
  label: string;
  hint: string;
}[] = [
  {
    value: "tsv",
    label: "Tab-separated",
    hint: "Pastes into Excel or Google Sheets as columns",
  },
  { value: "csv", label: "CSV", hint: "Comma-separated, quoted where needed" },
  {
    value: "markdown",
    label: "Markdown table",
    hint: "For tickets, docs and chat",
  },
  { value: "json", label: "JSON", hint: "An array with one object per row" },
  {
    value: "list",
    label: "One-line list",
    hint: "Values joined on one line, e.g. for a SQL IN (…) list",
  },
];

export interface CopyColumn {
  field: string;
  label: string;
}

/** One entry per selected row, one value per column, in column order. */
export type CopyRows = unknown[][];

const isEmpty = (value: unknown) =>
  value === null || value === undefined || value === "";

/**
 * Text for one cell value, or null when the cell is empty. Arrays (multi-select
 * columns) are joined with ", "; option objects use their label; other objects
 * fall back to JSON.
 */
export function stringifyCellValue(value: unknown): string | null {
  if (isEmpty(value)) return null;
  if (typeof value === "string") return value;
  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value);
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }
  if (Array.isArray(value)) {
    const parts = value
      .map(stringifyCellValue)
      .filter((part): part is string => part !== null);
    return parts.length ? parts.join(", ") : null;
  }
  if (typeof value === "object") {
    const label = (value as { label?: unknown }).label;
    if (typeof label === "string" || typeof label === "number") {
      return String(label);
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

const headerText = (column: CopyColumn, options: AdvancedCopyOptions) =>
  options.headerSource === "field" ? column.field : column.label;

/** Quotes a delimited (tsv/csv) field per the quote mode. */
function quoteDelimited(
  text: string,
  delimiter: string,
  quoteMode: QuoteMode
): string {
  if (quoteMode === "never") {
    // Keep the table shape: a delimiter or line break inside a value would
    // otherwise split it into extra columns or rows.
    return text
      .split(delimiter)
      .join(" ")
      .replace(/\r?\n|\r/g, " ");
  }
  const needsQuotes =
    quoteMode === "always" ||
    text.includes(delimiter) ||
    text.includes('"') ||
    /[\r\n]/.test(text);
  return needsQuotes ? `"${text.replace(/"/g, '""')}"` : text;
}

function serializeDelimited(
  columns: CopyColumn[],
  rows: CopyRows,
  options: AdvancedCopyOptions,
  delimiter: string
): string {
  const lines: string[] = [];
  const cell = (text: string) =>
    quoteDelimited(text, delimiter, options.quoteMode);

  if (options.includeHeader) {
    const header = columns.map((c) => cell(headerText(c, options)));
    if (options.includeRowNumbers) header.unshift(cell("#"));
    lines.push(header.join(delimiter));
  }
  rows.forEach((row, index) => {
    const values = row.map((value) =>
      cell(stringifyCellValue(value) ?? options.nullText)
    );
    if (options.includeRowNumbers) values.unshift(cell(String(index + 1)));
    lines.push(values.join(delimiter));
  });
  // CRLF, as spreadsheets write it and MUI X's own CSV export uses
  return lines.join("\r\n");
}

const escapeMarkdown = (text: string) =>
  text
    .replace(/\\/g, "\\\\")
    .replace(/\|/g, "\\|")
    .replace(/\r?\n|\r/g, "<br>");

function serializeMarkdown(
  columns: CopyColumn[],
  rows: CopyRows,
  options: AdvancedCopyOptions
): string {
  // A Markdown table always needs its header row
  const header = columns.map((c) => escapeMarkdown(headerText(c, options)));
  const body = rows.map((row, index) => {
    const values = row.map((value) =>
      escapeMarkdown(stringifyCellValue(value) ?? options.nullText)
    );
    if (options.includeRowNumbers) values.unshift(String(index + 1));
    return values;
  });
  if (options.includeRowNumbers) header.unshift("#");
  const line = (cells: string[]) => `| ${cells.join(" | ")} |`;
  return [line(header), line(header.map(() => "---")), ...body.map(line)].join(
    "\n"
  );
}

/** Raw numbers and booleans stay typed in JSON; empty cells become null. */
function jsonValue(value: unknown, options: AdvancedCopyOptions): unknown {
  if (isEmpty(value)) return null;
  if (
    options.valueSource === "raw" &&
    (typeof value === "number" || typeof value === "boolean")
  ) {
    return value;
  }
  return stringifyCellValue(value);
}

function serializeJson(
  columns: CopyColumn[],
  rows: CopyRows,
  options: AdvancedCopyOptions
): string {
  // Two columns can share a name; number the repeats so no value is lost
  const seen = new Map<string, number>();
  const keys = columns.map((c) => {
    const base = headerText(c, options);
    const count = (seen.get(base) ?? 0) + 1;
    seen.set(base, count);
    return count === 1 ? base : `${base} (${count})`;
  });
  const objects = rows.map((row) => {
    const object: Record<string, unknown> = {};
    keys.forEach((key, i) => {
      object[key] = jsonValue(row[i], options);
    });
    return object;
  });
  return JSON.stringify(objects, null, 2);
}

function serializeList(rows: CopyRows, options: AdvancedCopyOptions): string {
  const quote =
    options.listQuote === "single"
      ? "'"
      : options.listQuote === "double"
      ? '"'
      : "";
  const values: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    for (const value of row) {
      const text = stringifyCellValue(value) ?? options.nullText;
      // Empty cells are skipped unless the user asked for a placeholder
      if (text === "") continue;
      if (options.distinct) {
        if (seen.has(text)) continue;
        seen.add(text);
      }
      values.push(
        quote
          ? `${quote}${text.split(quote).join(quote + quote)}${quote}`
          : text
      );
    }
  }
  return values.join(options.listSeparator);
}

/** The clipboard text for the selected columns and rows. */
export function serializeAdvancedCopy(
  columns: CopyColumn[],
  rows: CopyRows,
  options: AdvancedCopyOptions
): string {
  if (columns.length === 0) return "";
  switch (options.format) {
    case "tsv":
      return serializeDelimited(columns, rows, options, "\t");
    case "csv":
      return serializeDelimited(columns, rows, options, ",");
    case "markdown":
      return serializeMarkdown(columns, rows, options);
    case "json":
      return serializeJson(columns, rows, options);
    case "list":
      return serializeList(rows, options);
    default:
      return "";
  }
}

/**
 * Merges stored options over the defaults, keeping only known keys with the
 * right type, so an old or hand-edited entry can't break the dialog.
 */
export function sanitizeAdvancedCopyOptions(
  stored: unknown
): AdvancedCopyOptions {
  const result = { ...DEFAULT_ADVANCED_COPY_OPTIONS };
  if (!stored || typeof stored !== "object") return result;
  const source = stored as Record<string, unknown>;
  (Object.keys(result) as (keyof AdvancedCopyOptions)[]).forEach((key) => {
    if (typeof source[key] === typeof result[key]) {
      (result as Record<string, unknown>)[key] = source[key];
    }
  });
  if (!COPY_FORMATS.some((f) => f.value === result.format)) {
    result.format = DEFAULT_ADVANCED_COPY_OPTIONS.format;
  }
  if (!["auto", "always", "never"].includes(result.quoteMode)) {
    result.quoteMode = DEFAULT_ADVANCED_COPY_OPTIONS.quoteMode;
  }
  if (!["none", "single", "double"].includes(result.listQuote)) {
    result.listQuote = DEFAULT_ADVANCED_COPY_OPTIONS.listQuote;
  }
  if (!["label", "field"].includes(result.headerSource)) {
    result.headerSource = DEFAULT_ADVANCED_COPY_OPTIONS.headerSource;
  }
  if (!["formatted", "raw"].includes(result.valueSource)) {
    result.valueSource = DEFAULT_ADVANCED_COPY_OPTIONS.valueSource;
  }
  return result;
}
